import { isPlatformBrowser } from '@angular/common';
import { Component, effect, inject, PLATFORM_ID, viewChildren } from '@angular/core';
import type { ElementRef } from '@angular/core';

interface TeamMember {
  readonly name: string;
  readonly role: string;
  readonly image: string;
}

@Component({
  selector: 'app-our-team',
  templateUrl: './our-team.html',
  styleUrl: './our-team.scss',
})
export class OurTeam {
  private readonly teamCards = viewChildren<ElementRef<HTMLElement>>('teamCard');
  private readonly platformId = inject(PLATFORM_ID);

  constructor() {
    effect((onCleanup) => {
      if (!isPlatformBrowser(this.platformId)) {
        return;
      }

      const cards = this.teamCards();
      let frameId = 0;

      // Reveal each card as its top edge rises past a trigger line near the bottom of the
      // viewport, and keep it revealed once it scrolls above. Scrolling back down lowers the
      // edge below the line and reverses the reveal.
      const render = (): void => {
        frameId = 0;

        const triggerLine = window.innerHeight * 0.85;

        cards.forEach(({ nativeElement }) => {
          const { top } = nativeElement.getBoundingClientRect();
          nativeElement.classList.toggle('our-team__card--visible', top <= triggerLine);
        });
      };

      // Coalesce bursts of scroll/resize events into a single update per frame.
      const schedule = (): void => {
        frameId ||= requestAnimationFrame(render);
      };

      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule);
      render();

      onCleanup(() => {
        window.removeEventListener('scroll', schedule);
        window.removeEventListener('resize', schedule);
        cancelAnimationFrame(frameId);
      });
    });
  }

  protected readonly teamMembers: readonly TeamMember[] = [
    {
      name: 'PINAR DEMIRDAG',
      role: 'FOUNDER/CEO',
      image: '/assets/origin/team/pinar-demirdag.png',
    },
    {
      name: 'DENYS PERIEL',
      role: 'HEAD ENGINEER',
      image: '/assets/origin/team/denys-periel.png',
    },
    {
      name: 'DADE ORGERON',
      role: 'GTM/STRATEGY',
      image: '/assets/origin/team/dade-orgeron.png',
    },
    {
      name: 'BARIS YILMAZ',
      role: 'PRODUCT / R&D',
      image: '/assets/origin/team/baris-yilmaz.png',
    },
    {
      name: 'DEN TARASOV',
      role: 'ENG. MANAGER',
      image: '/assets/origin/team/den-tasarov.png',
    },
    {
      name: 'AWAIS AMIN',
      role: 'ML ENGINEER',
      image: '/assets/origin/team/awais-amin.png',
    },
    {
      name: 'ARSLAN TARIQ',
      role: '3D FRONTEND ENGINEER',
      image: '/assets/origin/team/arslan-tariq.png',
    },
    {
      name: 'YURII TYRPYCH',
      role: 'FULL STACK ENGINEER',
      image: '/assets/origin/team/yurii-tyrpych.png',
    },
    {
      name: 'IRA DOBINOVA',
      role: 'UI/UX',
      image: '/assets/origin/team/ira-dobinova.png',
    },
    {
      name: 'SUJITH MOHAN',
      role: 'CLOUD & SECURITY',
      image: '/assets/origin/team/sujith-mohan.png',
    },
    {
      name: 'MARIA JULIANO',
      role: 'FINANCIAL CONTROLLER',
      image: '/assets/origin/team/maria-juliano.png',
    },
    {
      name: 'SEYMA OZGE',
      role: 'ADMIN AND SUPPORT',
      image: '/assets/origin/team/seyma-ozge.png',
    },
    {
      name: 'MIKE RUGGIO',
      role: 'COMMUNITY',
      image: '/assets/origin/team/mike-ruggio.png',
    },
  ];
}
